import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/600.css'
import '@fontsource/roboto/700.css'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  BadmintonShuttleIcon,
  Basketball01Icon,
  BoxingGloveIcon,
  ChampionIcon,
  Clapping01Icon,
  FootballIcon,
  GloveIcon,
  RunningShoesIcon,
  Shield01Icon,
  Target02Icon,
  TennisBallIcon,
  TennisRacketIcon,
  WhistleIcon,
} from '@hugeicons/core-free-icons'
import { ChevronDown, ChevronRight, Clapperboard, Download, Key, Link2, Maximize2, MessageCircle, MoreHorizontal, Play, Pointer, Share2, Unlink } from 'lucide-react'
import { FaBasketballBall, FaFutbol, FaInstagram } from 'react-icons/fa'
import {
  IoChevronBack,
  IoFootball,
  IoHandLeft,
  IoLocate,
  IoRepeat,
  IoShieldCheckmark,
  IoStar,
  IoStatsChart,
  IoStop,
  IoWalk,
} from 'react-icons/io5'
import { MdBarChart, MdDirectionsRun, MdEmojiPeople, MdGpsFixed, MdOutlineVerifiedUser, MdSportsBasketball, MdSportsTennis, MdSync } from 'react-icons/md'
import { GiCrossedSwords } from 'react-icons/gi'
import { LuFeather } from 'react-icons/lu'
import { SAMPLE_GAMES } from './matchPreviewSamples'
import { usePreviewAutoplay } from './usePreviewAutoplay'
import { HintBubble, PreviewHintStyles } from './PreviewHint'

/* Device geometry in iPhone points; the whole screen is scaled into the frame. */
const W = 393
const H = 852
const STATUS_H = 47
const TOP_BAR_H = 50
const STICKY_H = STATUS_H + 4 + TOP_BAR_H
const TABS_H = 44
const GUTTER = 8

const FONT = "Roboto, system-ui, -apple-system, 'Segoe UI', sans-serif"
const EASE = 'cubic-bezier(0.25, 1, 0.5, 1)'

const TABS = [
  { id: 'mine', label: 'My stats' },
  { id: 'team', label: 'Team stats' },
  { id: 'highlights', label: 'Highlights' },
  { id: 'lineups', label: 'Lineups' },
  { id: 'awards', label: 'Awards' },
]

const PAGE = {
  bg: '#F0F1F3',
  surface: '#FFFFFF',
  surfaceElev: '#F5F5F4',
  surfaceMute: '#FAFAF9',
  divider: '#E7E5E4',
  border: '#D6D3D1',
  text: '#0C0A09',
  textMuted: '#57534E',
  textDim: '#78716C',
  winner: '#16A34A',
}

const C = {
  attackingRed: '#EF4444',
  playmakingBlue: '#2e5fc7',
  defensiveAmber: '#FBBF24',
  technicalPurple: '#A78BFA',
  trophyGold: '#FFD700',
  servingOrange: '#F97316',
  rallyingGreen: '#22C55E',
  finessePurple: '#8B5CF6',
  label: '#6B7280',
  ink: '#1A1C1F',
}

const HOME_COLOR = '#14B8A6'
const AWAY_COLOR = '#8B5CF6'
const BAR_HOME = '#2FAE4C'
const BAR_AWAY = '#4C6FE8'

const CARD = {
  background: PAGE.surface,
  borderRadius: 16,
  padding: 16,
  border: `0.5px solid ${PAGE.border}`,
  boxShadow: '0 2px 8px rgba(12,10,9,0.06)',
}

const GLASS = {
  background: 'rgba(255,255,255,0.14)',
  border: '0.5px solid rgba(255,255,255,0.28)',
  backdropFilter: 'blur(14px) saturate(170%)',
  WebkitBackdropFilter: 'blur(14px) saturate(170%)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.32), inset 0 -1px 0 rgba(0,0,0,0.14), 0 6px 18px rgba(0,0,0,0.2)',
}

const CLIP_TYPES = {
  goal: ['Goal', '#10b981'],
  assist: ['Assist', '#3b82f6'],
  chance: ['Chance created', '#3b82f6'],
  dribble: ['Successful dribbles', '#06b6d4'],
  ace: ['Ace', '#2563eb'],
  winner: ['Winner', '#10b981'],
  smash: ['Smash', '#f59e0b'],
  lob: ['Lob', '#14b8a6'],
  long_rally: ['Long rally', '#0ea5e9'],
  highlight: ['Highlight', '#f97316'],
}

const AWARD_ICONS = {
  ChampionIcon,
  FootballIcon,
  Clapping01Icon,
  RunningShoesIcon,
  Shield01Icon,
  GloveIcon,
  Target02Icon,
  Basketball01Icon,
  BoxingGloveIcon,
  BadmintonShuttleIcon,
  TennisRacketIcon,
  WhistleIcon,
}

const RANK_COLORS = {
  match_rating: '#14B8A6',
  goals: '#EF4444',
  assists: '#3B82F6',
  shots_on_target: '#F59E0B',
  total_shots: '#F97316',
  chances_created: '#8B5CF6',
  successful_dribbles: '#EC4899',
  saves: '#22C55E',
  successful_tib: '#14B8A6',
  points: '#EF4444',
  three_made: '#8B5CF6',
  fg_made: '#3B82F6',
  ft_made: '#22C55E',
  offensive_rebound: '#F59E0B',
  defensive_rebound: '#14B8A6',
  blocks: '#EF4444',
  turnovers: '#6B7280',
  winner: '#EC4899',
  ace: '#F59E0B',
  smash: '#EF4444',
  dropshot: '#8B5CF6',
  lob: '#14B8A6',
  return: '#3B82F6',
}

function rankGlyph(key) {
  if (key === 'match_rating') return IoStar
  if (key.includes('goal')) return IoFootball
  if (key.includes('assist')) return IoHandLeft
  if (key.includes('shot')) return IoLocate
  if (key.includes('save')) return IoShieldCheckmark
  if (key.includes('dribble')) return IoWalk
  if (key.includes('point') || key.includes('made')) return IoStar
  if (key.includes('block')) return IoStop
  if (key.includes('rebound')) return IoRepeat
  return IoStatsChart
}

function ratingColor(rating) {
  if (rating >= 8) return '#1D4ED8'
  if (rating >= 7) return '#166534'
  if (rating >= 6.5) return '#3F6212'
  if (rating >= 6) return '#854D0E'
  if (rating >= 5) return '#9A3412'
  return '#991B1B'
}

const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0)

function mix(hex, target, amount) {
  const parse = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16))
  const [a, b] = [parse(hex), parse(target)]
  return `rgb(${a.map((v, i) => Math.round(v + (b[i] - v) * amount)).join(',')})`
}

function alpha(hex, a) {
  return `${hex}${Math.round(a * 255).toString(16).padStart(2, '0')}`
}

/* ------------------------------------------------------------------ chrome */

function StatusBar() {
  return (
    <div style={{ position: 'absolute', inset: '0 0 auto 0', height: STATUS_H, zIndex: 30, pointerEvents: 'none', color: '#fff' }}>
      <span style={{ position: 'absolute', left: 52, top: 16, fontFamily: "-apple-system, 'SF Pro Text', system-ui, sans-serif", fontSize: 17, fontWeight: 600, letterSpacing: -0.4 }}>9:41</span>
      <span style={{ position: 'absolute', left: (W - 124) / 2, top: 11, width: 124, height: 36, borderRadius: 20, background: '#000' }} />
      <svg style={{ position: 'absolute', right: 30, top: 19 }} width="78" height="13" viewBox="0 0 78 13" aria-hidden>
        <g fill="#fff">
          <rect x="0" y="8" width="3" height="4" rx="1" />
          <rect x="5" y="6" width="3" height="6" rx="1" />
          <rect x="10" y="3.5" width="3" height="8.5" rx="1" />
          <rect x="15" y="1" width="3" height="11" rx="1" />
          <path d="M31 3.2c2.2 0 4.2.9 5.7 2.3l1.1-1.1A9.6 9.6 0 0 0 31 1.6c-2.6 0-5 1-6.8 2.8l1.1 1.1A8 8 0 0 1 31 3.2Zm0 3.2c1.3 0 2.5.5 3.4 1.4l1.1-1.1a6.4 6.4 0 0 0-9 0l1.1 1.1c.9-.9 2.1-1.4 3.4-1.4Zm0 3.1c-.5 0-1 .2-1.3.5L31 11.4l1.3-1.4c-.3-.3-.8-.5-1.3-.5Z" />
          <rect x="47.5" y="0.5" width="24" height="12" rx="3.5" fill="none" stroke="#fff" strokeOpacity="0.4" />
          <rect x="49.5" y="2.5" width="18" height="8" rx="2" />
          <path d="M73 4.5v4c.8-.3 1.3-1.1 1.3-2s-.5-1.7-1.3-2Z" fillOpacity="0.45" />
        </g>
      </svg>
    </div>
  )
}

function GlassBackButton() {
  return (
    <span style={{ ...GLASS, width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-hidden>
      <IoChevronBack size={20} color="#fff" style={{ marginLeft: -2 }} />
    </span>
  )
}

const INVITE_ACTIONS = [
  ['Share', Share2],
  ['Copy', Link2],
  ['Delete', Unlink],
]

function InvitePill({ folded, onOpen }) {
  const transition = `all 300ms ${EASE}`
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Match actions"
      style={{
        ...GLASS,
        position: 'relative',
        width: folded ? 40 : 168,
        height: folded ? 40 : 50,
        borderRadius: 25,
        padding: 0,
        overflow: 'hidden',
        cursor: 'pointer',
        transition,
      }}
    >
      <span style={{ position: 'absolute', inset: 0, display: 'flex', opacity: folded ? 0 : 1, transform: `translateX(${folded ? 42 : 0}px)`, transition }}>
        {INVITE_ACTIONS.map(([label, Icon]) => (
          <span key={label} style={{ width: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Icon size={16} color="#fff" strokeWidth={2.4} />
            <span style={{ fontSize: 10, lineHeight: '12px', fontWeight: 500, color: '#fff' }}>{label}</span>
          </span>
        ))}
      </span>
      <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: folded ? 1 : 0, transform: `scale(${folded ? 1 : 0.86})`, transition }}>
        <MoreHorizontal size={22} color="#fff" strokeWidth={2.5} />
      </span>
    </button>
  )
}

function InviteMenu({ open, keyCode, onClose }) {
  const rows = [
    { icon: <MessageCircle size={16} color="#25D366" strokeWidth={2.5} />, tint: 'rgba(37,211,102,0.12)', title: 'Invite to game', hint: 'Share via WhatsApp or other apps' },
    { icon: <Key size={16} color={C.playmakingBlue} strokeWidth={2.5} />, tint: 'rgba(37,99,235,0.1)', title: 'Match key code', code: keyCode, hint: 'Tap to copy share link' },
    { icon: <Unlink size={16} color="#ED4956" strokeWidth={2.5} />, tint: 'rgba(237,73,86,0.1)', title: 'Delete', hint: 'Remove yourself and delete your stats' },
  ]
  return (
    <div
      onClick={onClose}
      style={{ position: 'absolute', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.25)', opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none', transition: `opacity 200ms ${EASE}` }}
    >
      <div
        role="menu"
        style={{
          position: 'absolute',
          top: STICKY_H + 4,
          right: GUTTER,
          minWidth: 260,
          padding: 6,
          borderRadius: 16,
          background: 'rgba(255,255,255,0.92)',
          border: '0.5px solid rgba(0,0,0,0.08)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: '0 18px 40px rgba(0,0,0,0.22)',
          transformOrigin: 'top right',
          transform: open ? 'scale(1)' : 'scale(0.92)',
          transition: `transform 240ms ${EASE}`,
        }}
      >
        {rows.map((row) => (
          <div key={row.title} role="menuitem" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 10px', borderRadius: 12 }}>
            <span style={{ width: 32, height: 32, borderRadius: 10, background: row.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{row.icon}</span>
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{row.title}</span>
              {row.code ? <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: 1, color: C.playmakingBlue }}>{row.code}</span> : null}
              <span style={{ fontSize: 12, color: '#6B7280', marginTop: 1 }}>{row.hint}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ header */

function Crest({ name, color, generic }) {
  if (generic) {
    return (
      <span style={{ width: 52, height: 52, borderRadius: 26, background: 'rgba(255,255,255,0.08)', border: `1.5px solid ${mix(color, '#F4F4F5', 0.62)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, fontWeight: 600 }}>
        {name.slice(-1)}
      </span>
    )
  }
  return (
    <span style={{ width: 52, height: 52, borderRadius: 12, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, fontWeight: 700, boxShadow: 'inset 0 -6px 12px rgba(0,0,0,0.18)' }}>
      {name.charAt(0)}
    </span>
  )
}

function TeamName({ name, color, generic }) {
  if (generic) {
    return (
      <span style={{ marginTop: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <span style={{ fontSize: 11, letterSpacing: 0.6, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase' }}>Team</span>
        <span style={{ fontSize: 18, fontWeight: 600, color: mix(color, '#FFFFFF', 0.35) }}>{name.slice(-1)}</span>
      </span>
    )
  }
  return <span style={{ marginTop: 10, fontSize: 15, lineHeight: '19px', fontWeight: 700, color: '#fff', textAlign: 'center' }}>{name}</span>
}

function SportBall({ sport, size, color }) {
  const icon = sport === 'basketball' ? Basketball01Icon : sport === 'padel' ? TennisBallIcon : FootballIcon
  return <HugeiconsIcon icon={icon} size={size} color={color} strokeWidth={1.8} />
}

function MatchHeader({ game, t, onMeasure }) {
  const ref = useRef(null)
  useLayoutEffect(() => {
    if (ref.current) onMeasure(ref.current.offsetHeight)
  }, [game, onMeasure])

  const fade = Math.max(0, 1 - t / 0.7)
  const crestScale = 1 - 0.56 * t
  const sideStyle = { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 8 }
  const homeLines = game.scorersHome ?? game.pointsHome ?? []
  const awayLines = game.scorersAway ?? game.pointsAway ?? []
  const pointsMode = !game.scorersHome

  const scorerLine = (line, align) =>
    pointsMode ? (
      <span key={line.name} style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: align, fontSize: 11, fontWeight: 500, lineHeight: '15px', color: line.isYou ? '#FBBF24' : 'rgba(255,255,255,0.88)' }}>
        {line.name}
        <span style={{ fontSize: 9 }}>{game.sport === 'padel' ? '🎾' : '🏀'}</span>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>{line.valueLine}</span>
      </span>
    ) : (
      <span key={line.name} style={{ fontSize: 12, fontWeight: 500, lineHeight: '16px', color: line.isYou ? '#FBBF24' : 'rgba(255,255,255,0.88)', textAlign: align === 'flex-end' ? 'right' : 'left' }}>
        {line.name} {line.times}
      </span>
    )

  return (
    <div ref={ref} style={{ padding: '18px 8px 14px', transform: `translateY(${-54 * t}px)` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <div style={sideStyle}>
          <span style={{ transform: `translateX(${76 * t}px) scale(${crestScale})` }}>
            <Crest name={game.home} color={HOME_COLOR} generic={game.generic} />
          </span>
          <span style={{ opacity: fade }}>
            <TeamName name={game.home} color={HOME_COLOR} generic={game.generic} />
          </span>
        </div>
        <div style={{ minWidth: 108, margin: '0 16px', paddingTop: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: 34, lineHeight: '38px', fontWeight: 700, letterSpacing: 1, color: '#fff', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', transform: `scale(${1 - 0.52 * t})` }}>
            {game.score}
          </span>
          <span style={{ marginTop: 3, fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.55)', opacity: fade }}>Full time</span>
        </div>
        <div style={sideStyle}>
          <span style={{ transform: `translateX(${-76 * t}px) scale(${crestScale})` }}>
            <Crest name={game.away} color={AWAY_COLOR} generic={game.generic} />
          </span>
          <span style={{ opacity: fade }}>
            <TeamName name={game.away} color={AWAY_COLOR} generic={game.generic} />
          </span>
        </div>
      </div>

      <div style={{ opacity: fade, transform: `translateY(${-22 * t}px)` }}>
        {homeLines.length || awayLines.length ? (
          <div style={{ marginTop: 14, display: 'flex', gap: 4 }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              {homeLines.map((line) => scorerLine(line, 'flex-end'))}
              {game.splitHome ? <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>Serve {game.splitHome.serve} · Return {game.splitHome.returnPts}</span> : null}
            </div>
            <div style={{ width: 16, display: 'flex', justifyContent: 'center', paddingTop: 2 }}>
              <SportBall sport={game.sport} size={11} color="rgba(255,255,255,0.8)" />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              {awayLines.map((line) => scorerLine(line, 'flex-start'))}
              {game.splitAway ? <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>Serve {game.splitAway.serve} · Return {game.splitAway.returnPts}</span> : null}
            </div>
          </div>
        ) : null}
        {game.quarters ? (
          <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center', gap: 6 }}>
            {game.quarters.map(([label, a, b]) => (
              <span key={label} style={{ minWidth: 52, borderRadius: 10, padding: '6px 8px', background: 'rgba(255,255,255,0.10)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>{label}</span>
                <span style={{ marginTop: 2, fontSize: 12, fontWeight: 600, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                  {a}-{b}
                </span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function TabBar({ tab, onSelect }) {
  const labelRefs = useRef([])
  const [widths, setWidths] = useState([])
  useLayoutEffect(() => {
    setWidths(labelRefs.current.map((node) => node?.offsetWidth ?? 0))
  }, [])
  const col = W / TABS.length
  const lw = widths[tab] ?? 0

  return (
    <div role="tablist" aria-label="Match details" style={{ position: 'relative', height: TABS_H, display: 'flex', borderBottom: '0.5px solid rgba(255,255,255,0.16)' }}>
      {TABS.map(({ id, label }, i) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={tab === i}
          onClick={() => onSelect(i)}
          style={{ flex: 1, minWidth: 0, padding: '10px 0 12px', background: 'none', border: 0, cursor: 'pointer', display: 'flex', justifyContent: 'center', position: 'relative' }}
        >
          <span
            ref={(node) => {
              labelRefs.current[i] = node
            }}
            style={{ fontSize: 14, lineHeight: '18px', fontWeight: 600, letterSpacing: -0.15, whiteSpace: 'nowrap', color: tab === i ? '#fff' : 'rgba(255,255,255,0.55)', transition: `color 200ms ${EASE}` }}
          >
            {label}
          </span>
        </button>
      ))}
      <span style={{ position: 'absolute', bottom: 0, height: 2, borderRadius: 1, background: '#fff', left: tab * col + (col - lw) / 2, width: lw, transition: `left 280ms ${EASE}, width 280ms ${EASE}` }} />
    </div>
  )
}

/* ---------------------------------------------------------------- my stats */

function Section({ icon: Icon, color, title, children }) {
  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <Icon size={18} color={color} />
        <span style={{ marginLeft: 8, fontSize: 15, fontWeight: 600, color }}>{title}</span>
      </div>
      {children}
    </div>
  )
}

function Huge({ label, value, center }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: center ? 'center' : 'flex-start', marginBottom: 10 }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: C.label }}>{label}</span>
      <span style={{ fontSize: 26, lineHeight: '34px', fontWeight: 700, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  )
}

function Pair({ items, compact }) {
  return (
    <div style={{ display: 'flex', marginBottom: 6 }}>
      {items.map(([label, value]) =>
        compact ? (
          <div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 22, lineHeight: '28px', fontWeight: 700, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
            <span style={{ fontSize: 10, fontWeight: 500, color: C.label }}>{label}</span>
          </div>
        ) : (
          <div key={label} style={{ flex: 1 }}>
            <Huge label={label} value={value} center />
          </div>
        ),
      )}
    </div>
  )
}

function Progress({ label, color, value, left, right }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: C.ink }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color }}>{value}%</span>
      </div>
      <div style={{ height: 5, borderRadius: 3, background: '#F3F4F6', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${value}%`, borderRadius: 3, background: color }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 500, color }}>{left}</span>
        <span style={{ fontSize: 11, fontWeight: 500, color: C.label }}>{right}</span>
      </div>
    </div>
  )
}

function FootballStats({ s }) {
  const onTarget = s.shots_on_target + s.goals
  const shots = onTarget + s.shots_off_target
  const chances = s.assists + s.chances_created
  return (
    <>
      <Section icon={FaFutbol} color={C.attackingRed} title="Shooting">
        <Huge label="Goals" value={s.goals} />
        <Progress label="Shot Accuracy" color={C.attackingRed} value={pct(onTarget, shots)} left={`${onTarget} On Target`} right={`${s.shots_off_target} Off Target`} />
        <Progress label="Conversion Rate" color={C.attackingRed} value={pct(s.goals, onTarget)} left={`${s.goals} Goals`} right={`${onTarget} On Target`} />
        <Progress label="Shot Conversion" color={C.attackingRed} value={pct(s.goals, shots)} left={`${s.goals} Goals`} right={`${shots} Shots`} />
      </Section>
      <Section icon={MdEmojiPeople} color={C.playmakingBlue} title="Playmaking">
        <Pair items={[['Assists', s.assists], ['Chances', s.chances_created]]} />
        <Progress label="Chance Creation" color={C.playmakingBlue} value={pct(s.assists, chances)} left={`${s.assists} Assists`} right={`${chances} Total Chances`} />
      </Section>
      <Section icon={MdOutlineVerifiedUser} color={C.defensiveAmber} title="Defense">
        <Pair compact items={[['Saves', s.saves], ['TIB Success', s.successful_tib]]} />
      </Section>
      <Section icon={MdDirectionsRun} color={C.technicalPurple} title="Technical">
        <Huge label="Successful Dribbles" value={s.successful_dribbles} />
      </Section>
    </>
  )
}

function BasketballStats({ s }) {
  const rebounds = s.offensive_rebound + s.defensive_rebound
  return (
    <>
      <Section icon={MdSportsBasketball} color={C.attackingRed} title="Scoring">
        <Huge label="Points" value={s.points} />
        <Progress label="Field Goal %" color={C.attackingRed} value={pct(s.fg_made, s.fg_attempted)} left={`${s.fg_made} Made`} right={`${s.fg_attempted} Attempts`} />
      </Section>
      <Section icon={MdGpsFixed} color={C.technicalPurple} title="Shooting">
        <Pair items={[['3-Pointers', `${s.three_made}/${s.three_attempted}`], ['Free Throws', `${s.ft_made}/${s.ft_attempted}`]]} />
        <Progress label="3-Point %" color={C.technicalPurple} value={pct(s.three_made, s.three_attempted)} left={`${s.three_made} Made`} right={`${s.three_attempted} Attempts`} />
        <Progress label="Free Throw %" color={C.technicalPurple} value={pct(s.ft_made, s.ft_attempted)} left={`${s.ft_made} Made`} right={`${s.ft_attempted} Attempts`} />
      </Section>
      <Section icon={MdEmojiPeople} color={C.playmakingBlue} title="Playmaking">
        <Pair items={[['Assists', s.assists], ['Turnovers', s.turnovers]]} />
        <Progress label="Ball Security" color={C.playmakingBlue} value={pct(s.assists, s.assists + s.turnovers)} left={`${s.assists} Assists`} right={`${s.turnovers} Turnovers`} />
      </Section>
      <Section icon={MdOutlineVerifiedUser} color={C.defensiveAmber} title="Defense">
        <Pair compact items={[['Rebounds', rebounds], ['Blocks', s.blocks]]} />
        <Pair compact items={[['Offensive', s.offensive_rebound], ['Defensive', s.defensive_rebound]]} />
        <Progress label="Defensive Boards" color={C.defensiveAmber} value={pct(s.defensive_rebound, rebounds)} left={`${s.defensive_rebound} Defensive`} right={`${rebounds} Rebounds`} />
      </Section>
    </>
  )
}

function PadelStats({ s }) {
  const total = s.serve + s.return
  const special = s.dropshot + s.lob + s.smash
  return (
    <>
      <Section icon={MdBarChart} color={C.trophyGold} title="Points won">
        <Huge label="Total Points" value={total} />
        <Progress label="Points Distribution" color={C.trophyGold} value={pct(s.serve, total)} left={`${s.serve} By Serve`} right={`${s.return} By Returns`} />
      </Section>
      <Section icon={MdSportsTennis} color={C.servingOrange} title="Serving">
        <Pair items={[['Aces', s.ace], ['Double Faults', s.double_faults]]} />
        <Progress label="Serve Reliability" color={C.servingOrange} value={pct(s.serve, s.serve + s.double_faults)} left={`${s.serve} Serve Points`} right={`${s.double_faults} Double Faults`} />
      </Section>
      <Section icon={GiCrossedSwords} color={C.attackingRed} title="Shot quality">
        <Pair items={[['Winners', s.winner], ['Unforced Errors', s.unforced_errors]]} />
        <Progress label="Attacking Efficiency" color={C.attackingRed} value={pct(s.winner, s.winner + s.unforced_errors)} left={`${s.winner} Winners`} right={`${s.unforced_errors} Errors`} />
      </Section>
      <Section icon={LuFeather} color={C.finessePurple} title="Special shots">
        <Pair compact items={[['Dropshots', s.dropshot], ['Lobs', s.lob], ['Smashes', s.smash]]} />
        <Progress label="Smash Share" color={C.finessePurple} value={pct(s.smash, special)} left={`${s.smash} Smashes`} right={`${special} Special Shots`} />
      </Section>
      <Section icon={MdSync} color={C.rallyingGreen} title="Rallying">
        <Huge label="Long Rallies" value={s.long_rally} />
      </Section>
    </>
  )
}

function MyStatsTab({ game }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '16px 16px 4px' }}>
        <span style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>Your match rating</span>
        <span style={{ minWidth: 44, height: 28, borderRadius: 8, background: ratingColor(game.rating), color: '#fff', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px' }}>
          {game.rating.toFixed(1)}
        </span>
      </div>
      <div style={{ padding: '8px 12px 16px' }}>
        {game.sport === 'football' ? <FootballStats s={game.mine} /> : game.sport === 'basketball' ? <BasketballStats s={game.mine} /> : <PadelStats s={game.mine} />}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------- team stats */

function catmull(points) {
  return points.reduce((d, [x, y], i, arr) => {
    if (i === 0) return `M${x},${y}`
    const [x0, y0] = arr[i - 2] ?? arr[i - 1]
    const [x1, y1] = arr[i - 1]
    const [x3, y3] = arr[i + 1] ?? [x, y]
    const c1 = [x1 + (x - x0) / 6, y1 + (y - y0) / 6]
    const c2 = [x - (x3 - x1) / 6, y - (y3 - y1) / 6]
    return `${d} C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${x},${y}`
  }, '')
}

function MomentumCard({ game }) {
  const id = React.useId().replace(/:/g, '')
  const width = W - 2 * GUTTER - 24
  const left = 42
  const right = width - 8
  const top = 20
  const half = 70
  const mid = top + half
  const height = top + half * 2 + 24
  const minutes = game.minutes
  const x = (m) => left + ((right - left) * m) / minutes

  const buckets = 12
  const raw = Array.from({ length: buckets }, () => 0)
  game.momentum.forEach(([type, minute, side]) => {
    const i = Math.min(buckets - 1, Math.floor((minute / minutes) * buckets))
    const weight = type === 'Goal' ? 3 : 1.5
    const sign = side === 'A' ? 1 : -1
    raw[i] += weight * sign
    if (i > 0) raw[i - 1] += 0.5 * weight * sign
    if (i < buckets - 1) raw[i + 1] += 0.4 * weight * sign
  })
  const peak = Math.max(1, ...raw.map(Math.abs))
  const points = [[left, mid], ...raw.map((v, i) => [x(((i + 0.5) * minutes) / buckets), mid - (v / peak) * half * 0.8]), [right, mid]]
  const line = catmull(points)
  const area = `${line} L${right},${mid} L${left},${mid} Z`
  const step = minutes > 50 ? 15 : 10
  const ticks = Array.from({ length: Math.floor(minutes / step) + 1 }, (_, i) => i * step)
  const balls = game.momentum.filter(([type]) => type === 'Goal' || game.sport === 'basketball')
  const Ball = game.sport === 'basketball' ? FaBasketballBall : FaFutbol

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '14px 12px 12px' }}>
      <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 500, color: 'rgba(17,24,39,0.85)', marginBottom: 6 }}>Momentum</div>
      <div style={{ position: 'relative', width, height }}>
        <svg width={width} height={height} style={{ display: 'block' }} aria-hidden>
          <defs>
            <clipPath id={`${id}-up`}><rect x={0} y={0} width={width} height={mid} /></clipPath>
            <clipPath id={`${id}-down`}><rect x={0} y={mid} width={width} height={height} /></clipPath>
          </defs>
          {[-half / 2, half / 2].map((dy) => (
            <line key={dy} x1={left} x2={right} y1={mid + dy} y2={mid + dy} stroke="rgba(17,24,39,0.1)" strokeDasharray="4 6" />
          ))}
          <path d={area} fill={HOME_COLOR} fillOpacity={0.28} clipPath={`url(#${id}-up)`} />
          <path d={area} fill={AWAY_COLOR} fillOpacity={0.28} clipPath={`url(#${id}-down)`} />
          <path d={line} fill="none" stroke="rgba(17,24,39,0.3)" strokeWidth={1.5} />
          <line x1={left} x2={right} y1={mid} y2={mid} stroke="rgba(17,24,39,0.35)" strokeDasharray="4 4" />
          <text x={8} y={mid - half / 2 + 4} fontSize={10} fontWeight={600} fill={HOME_COLOR}>{game.generic ? 'A' : game.home.slice(0, 3).toUpperCase()}</text>
          <text x={8} y={mid + half / 2 + 4} fontSize={10} fontWeight={600} fill={AWAY_COLOR}>{game.generic ? 'B' : game.away.slice(0, 3).toUpperCase()}</text>
          <line x1={left} x2={right} y1={mid + half + 4} y2={mid + half + 4} stroke="rgba(17,24,39,0.2)" strokeDasharray="3 5" />
          {ticks.map((m) => (
            <text key={m} x={x(m)} y={height - 4} fontSize={10} fill="rgba(17,24,39,0.5)" textAnchor={m === 0 ? 'start' : m === minutes ? 'end' : 'middle'}>
              {m}'
            </text>
          ))}
        </svg>
        {balls.map(([, minute, side], i) => (
          <span key={i} style={{ position: 'absolute', top: 0, left: x(minute) - 8, width: 16, height: 16, display: 'flex' }}>
            <Ball size={16} color={side === 'A' ? HOME_COLOR : AWAY_COLOR} />
          </span>
        ))}
      </div>
    </div>
  )
}

function OverviewCard({ game }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '18px 16px 8px' }}>
      <div style={{ textAlign: 'center', fontSize: 17, fontWeight: 600, color: C.ink, marginBottom: 18 }}>Match overview</div>
      {game.teamRows.map(([label, a, b]) => {
        const both = a > 0 && b > 0
        return (
          <div key={label} style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ width: 72, fontSize: 15, fontWeight: 600, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{a}</span>
              <span style={{ flex: 1, textAlign: 'center', fontSize: 14, fontWeight: 500, color: C.ink }}>{label}</span>
              <span style={{ width: 72, textAlign: 'right', fontSize: 15, fontWeight: 600, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{b}</span>
            </div>
            <div style={{ display: 'flex', gap: both ? 4 : 0, height: 6, borderRadius: 3, background: '#E6E8EE', overflow: 'hidden' }}>
              {a > 0 ? <span style={{ flex: both ? a : 1, background: BAR_HOME, borderRadius: 3 }} /> : null}
              {b > 0 ? <span style={{ flex: both ? b : 1, background: BAR_AWAY, borderRadius: 3 }} /> : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function Avatar({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" style={{ borderRadius: '50%', flexShrink: 0, display: 'block' }} aria-hidden>
      <circle cx="24" cy="24" r="24" fill="#E0E0E0" />
      <circle cx="24" cy="18" r="9" fill="#BDBDBD" />
      <path d="M24 29c-7.18 0-13 3.13-13 7v2h26v-2c0-3.87-5.82-7-13-7z" fill="#BDBDBD" />
    </svg>
  )
}

function ComparisonPanel({ game }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: '#4B5563', margin: '6px 4px 8px' }}>PLAYER COMPARISON</div>
      <div style={{ background: '#fff', borderRadius: 16, border: '0.5px solid rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px 10px', borderBottom: '0.5px solid rgba(17,24,39,0.06)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>Stat rankings</div>
          <div style={{ fontSize: 11, fontWeight: 500, color: '#4B5563', marginTop: 2 }}>Tap a stat to see the full leaderboard</div>
        </div>
        {game.rankings.map(({ key, label, decimals, players }, index) => {
          const accent = RANK_COLORS[key] ?? '#6B7280'
          const Glyph = rankGlyph(key)
          return (
            <div key={key} style={{ padding: '14px 16px 12px', borderTop: index ? '0.5px solid rgba(17,24,39,0.06)' : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ width: 32, height: 32, borderRadius: 10, background: alpha(accent, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Glyph size={16} color={accent} />
                </span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.ink }}>{label}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: accent }}>All ›</span>
              </div>
              {players.slice(0, 3).map((p, rank) => (
                <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
                  <span style={{ width: 22, height: 22, borderRadius: 11, background: rank === 0 ? accent : 'rgba(17,24,39,0.06)', color: rank === 0 ? '#fff' : '#4B5563', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {rank + 1}
                  </span>
                  <Avatar size={28} />
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: C.ink }}>
                    {p.name}
                    {p.name === game.you ? <span style={{ color: PAGE.textDim }}> · You</span> : null}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: rank === 0 ? accent : '#4B5563', fontVariantNumeric: 'tabular-nums' }}>
                    {decimals ? p.value.toFixed(decimals) : p.value}
                  </span>
                </div>
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TeamStatsTab({ game }) {
  return (
    <div style={{ padding: `14px ${GUTTER}px 0`, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <MomentumCard game={game} />
      <OverviewCard game={game} />
      <ComparisonPanel game={game} />
    </div>
  )
}

/* -------------------------------------------------------------- highlights */

const SCENE_PLAYERS = [
  [38, 52], [62, 34], [74, 60], [96, 44], [112, 64], [128, 38], [54, 70], [88, 26],
]

function Scene({ sport, variant = 0 }) {
  const zoom = [1.15, 1.45, 1.3, 1.6, 1.25, 1.5][variant % 6]
  const dx = [-6, -30, 4, -40, -16, -24][variant % 6]
  const dy = [-6, -14, -8, -18, -4, -12][variant % 6]
  const players = SCENE_PLAYERS.map(([px, py], i) => [px + ((variant * 7 + i * 3) % 9) - 4, py + ((variant * 5 + i) % 7) - 3, i % 2 ? AWAY_COLOR : HOME_COLOR])
  let court
  if (sport === 'basketball') {
    court = (
      <>
        <rect width="160" height="90" fill="#B8814C" />
        {Array.from({ length: 12 }, (_, i) => <line key={i} x1="0" x2="160" y1={i * 8} y2={i * 8} stroke="#9B6A3C" strokeWidth="0.4" />)}
        <rect x="56" y="0" width="48" height="40" fill="#8E3B2E" />
        <g fill="none" stroke="#F5F0E6" strokeWidth="0.9">
          <rect x="56" y="0" width="48" height="40" />
          <circle cx="80" cy="40" r="12" />
          <path d="M18 0 V22 A62 62 0 0 0 142 22 V0" />
          <line x1="0" x2="160" y1="88" y2="88" />
        </g>
        <rect x="70" y="2" width="20" height="1.6" fill="#fff" />
        <circle cx="80" cy="7" r="3" fill="none" stroke="#F97316" strokeWidth="0.9" />
      </>
    )
  } else if (sport === 'padel') {
    court = (
      <>
        <rect width="160" height="90" fill="#1F4E8C" />
        <rect x="10" y="6" width="140" height="80" fill="#2563A8" />
        <g fill="none" stroke="#F8FAFC" strokeWidth="0.9">
          <rect x="10" y="6" width="140" height="80" />
          <line x1="80" x2="80" y1="20" y2="72" />
          <line x1="30" x2="30" y1="6" y2="86" />
          <line x1="130" x2="130" y1="6" y2="86" />
        </g>
        <rect x="79" y="4" width="2" height="84" fill="#0F172A" opacity="0.75" />
        <rect x="0" y="0" width="160" height="6" fill="rgba(255,255,255,0.18)" />
      </>
    )
  } else {
    court = (
      <>
        <rect width="160" height="90" fill="#2F7D3A" />
        {Array.from({ length: 8 }, (_, i) => <rect key={i} x={i * 20} y="0" width="10" height="90" fill="#348A40" />)}
        <g fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="0.9">
          <rect x="4" y="4" width="152" height="82" />
          <line x1="80" x2="80" y1="4" y2="86" />
          <circle cx="80" cy="45" r="13" />
          <rect x="4" y="26" width="20" height="38" />
          <rect x="136" y="26" width="20" height="38" />
        </g>
      </>
    )
  }
  return (
    <svg viewBox="0 0 160 90" preserveAspectRatio="xMidYMid slice" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} aria-hidden>
      <g transform={`translate(${dx} ${dy}) scale(${zoom})`}>
        {court}
        {players.map(([px, py, color], i) => (
          <g key={i}>
            <ellipse cx={px} cy={py + 4.2} rx="2.4" ry="0.9" fill="rgba(0,0,0,0.3)" />
            <rect x={px - 1.5} y={py - 2.5} width="3" height="6" rx="1.4" fill={color} />
            <circle cx={px} cy={py - 3.6} r="1.3" fill="#F2D0B5" />
          </g>
        ))}
        <circle cx={92 + (variant % 3) * 6} cy={48 - (variant % 2) * 6} r="1.3" fill={sport === 'padel' ? '#E6F05A' : sport === 'basketball' ? '#F97316' : '#fff'} />
      </g>
      <defs>
        <radialGradient id="scene-vignette" cx="50%" cy="40%" r="75%">
          <stop offset="55%" stopColor="#000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.55" />
        </radialGradient>
      </defs>
      <rect width="160" height="90" fill="url(#scene-vignette)" />
    </svg>
  )
}

function Pill({ selected, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 36,
        padding: '0 16px',
        borderRadius: 18,
        background: 'rgba(255,255,255,0.85)',
        border: selected ? '1.5px solid #0C0A09' : '0.5px solid rgba(0,0,0,0.08)',
        fontSize: 14,
        fontWeight: selected ? 600 : 400,
        color: selected ? PAGE.text : PAGE.textMuted,
        cursor: 'pointer',
        fontFamily: FONT,
      }}
    >
      {children}
    </button>
  )
}

function HighlightsTab({ game }) {
  const [reel, setReel] = useState('mine')
  const halfMinute = game.minutes / 2
  const clips = game.clips.map((clip) => ({ ...clip, minute: parseInt(clip.time, 10) }))
  const firstSecondHalf = clips.findIndex((c) => c.minute >= halfMinute)

  const anchor = (text) => (
    <div key={text} style={{ position: 'relative', display: 'flex', alignItems: 'center', height: 28, marginBottom: 12 }}>
      <span style={{ position: 'absolute', left: 22, width: 10, height: 10, borderRadius: 5, background: '#e5e7eb', border: '2px solid #fff', boxSizing: 'border-box' }} />
      <span style={{ marginLeft: 66, fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: PAGE.textDim }}>{text}</span>
    </div>
  )

  const rows = []
  rows.push(anchor('Start of Game'))
  clips.forEach((clip, i) => {
    if (i === firstSecondHalf) rows.push(anchor(game.sport === 'football' ? 'Halftime' : 'Midgame'))
    const [chip, color] = CLIP_TYPES[clip.type]
    const railLabel = clip.label ?? chip
    rows.push(
      <div key={clip.time} style={{ display: 'flex', gap: 12, marginBottom: 12, position: 'relative' }}>
        <div style={{ width: 54, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8, gap: 4 }}>
          <span style={{ background: PAGE.bg, fontSize: 10, fontWeight: 600, color, textAlign: 'center', lineHeight: '13px', padding: '2px 0' }}>{railLabel}</span>
          <span style={{ background: 'rgba(17,24,39,0.08)', borderRadius: 8, padding: '2px 6px', fontSize: 10, fontWeight: 600, color: '#6B7280', fontVariantNumeric: 'tabular-nums' }}>{clip.time}</span>
        </div>
        <div style={{ flex: 1, minWidth: 0, borderRadius: 12, border: '1px solid rgba(0,0,0,0.06)', background: '#fff', overflow: 'hidden' }}>
          <div style={{ position: 'relative', height: 132, background: '#f3f4f6', overflow: 'hidden' }}>
            <Scene sport={game.sport} variant={i + 1} />
            <span style={{ position: 'absolute', top: 8, left: 8, background: color, color: '#fff', fontSize: 9, fontWeight: 700, padding: '3px 6px', borderRadius: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>{chip}</span>
            <span style={{ position: 'absolute', top: 8, right: 8, width: 32, height: 32, borderRadius: 16, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Maximize2 size={16} color="#fff" />
            </span>
            <span style={{ position: 'absolute', left: '50%', top: '50%', width: 40, height: 40, marginLeft: -20, marginTop: -20, borderRadius: 20, background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Play size={18} color="#111827" fill="#111827" style={{ marginLeft: 2 }} />
            </span>
            <span style={{ position: 'absolute', right: 8, bottom: 8, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 6 }}>{clip.duration}s</span>
          </div>
          <div style={{ display: 'flex', gap: 8, padding: '12px 12px 10px' }}>
            <span style={{ width: 48, height: 44, borderRadius: 12, background: '#f3f4f6', border: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Download size={18} color="#111827" />
            </span>
            <span style={{ width: 48, height: 44, borderRadius: 12, background: 'linear-gradient(45deg, #F77737, #F56040 30%, #E1306C 60%, #C13584)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaInstagram size={20} color="#fff" />
            </span>
          </div>
        </div>
      </div>,
    )
  })
  rows.push(anchor('End of Game'))

  return (
    <div style={{ padding: `14px ${GUTTER}px 0`, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <Pill selected={reel === 'mine'} onClick={() => setReel('mine')}>My highlight</Pill>
          <Pill selected={reel === 'game'} onClick={() => setReel('game')}>Game highlight</Pill>
        </div>
        <div style={{ position: 'relative', aspectRatio: '16 / 9', borderRadius: 16, overflow: 'hidden', background: '#111' }}>
          <Scene sport={game.sport} variant={reel === 'mine' ? 0 : 4} />
          <span style={{ position: 'absolute', top: 12, right: 12, width: 40, height: 40, borderRadius: 20, background: 'rgba(0,0,0,0.75)', border: '1.5px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Download size={18} color="#fff" />
          </span>
          <span style={{ position: 'absolute', left: '50%', top: '50%', width: 52, height: 52, marginLeft: -26, marginTop: -26, borderRadius: 26, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Play size={22} color="#fff" fill="#fff" style={{ marginLeft: 3 }} />
          </span>
          <div style={{ position: 'absolute', left: 12, right: 12, bottom: 10, display: 'flex', alignItems: 'center', gap: 8, color: '#fff', fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            <span>0:00</span>
            <span style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.35)' }}>
              <span style={{ display: 'block', width: '18%', height: '100%', borderRadius: 2, background: '#fff' }} />
            </span>
            <span>{reel === 'mine' ? game.myReel : game.reel}</span>
          </div>
        </div>
        <span style={{ fontSize: 13, fontWeight: 500, color: PAGE.textMuted }}>
          {game.venue} · {game.dateLabel} · {game.timeLabel}
        </span>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, minHeight: 64, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ width: 40, height: 40, borderRadius: 12, background: PAGE.surfaceMute, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Clapperboard size={20} color={PAGE.text} />
        </span>
        <span style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: PAGE.text }}>Post clips</span>
          <span style={{ fontSize: 13, color: PAGE.textMuted }}>Choose moments for your profile</span>
        </span>
        <ChevronRight size={20} color={PAGE.textDim} />
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: PAGE.text }}>Match events</span>
          <span style={{ height: 40, padding: '0 14px', borderRadius: 20, background: 'rgba(255,255,255,0.85)', border: '0.5px solid rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 500, color: PAGE.text }}>
            All events <ChevronDown size={16} color={PAGE.textMuted} />
          </span>
        </div>
        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: PAGE.textDim, marginBottom: 14 }}>
          Match timeline · {clips.length} moments
        </div>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 27, top: 14, bottom: 14, width: 1, background: 'rgba(0,0,0,0.08)' }} />
          {rows}
        </div>
      </div>
    </div>
  )
}

/* ----------------------------------------------------------------- lineups */

function LineupsTab({ game }) {
  const team = (side, name, color) => (
    <div key={side} style={{ ...CARD, paddingTop: 0, overflow: 'hidden' }}>
      <div style={{ height: 3, margin: '0 -16px', background: color, borderTopLeftRadius: 14, borderTopRightRadius: 14 }} />
      <div style={{ padding: '6px 0 10px', fontSize: 14, fontWeight: 700, color: PAGE.text, marginTop: 6 }}>
        {game.generic ? (
          <>
            <span style={{ color: PAGE.textMuted, fontWeight: 500 }}>Team </span>
            <span style={{ color }}>{name.slice(-1)}</span>
          </>
        ) : (
          name
        )}
      </div>
      {game.players
        .filter((p) => p.side === side)
        .map((p) => {
          const you = p.name === game.you
          return (
            <div key={p.name} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderTop: `0.5px solid ${PAGE.divider}` }}>
              <Avatar size={36} />
              <span style={{ flex: 1, marginLeft: 10, fontSize: 14, fontWeight: you ? 600 : 500, color: PAGE.text }}>
                {p.name}
                {you ? <span style={{ color: PAGE.textMuted }}> ·  YOU</span> : null}
              </span>
              <span style={{ display: 'flex', gap: 6 }}>
                {p.s.goals > 0 ? (
                  <span style={{ height: 28, minWidth: 52, borderRadius: 8, background: PAGE.surfaceElev, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '0 8px' }}>
                    <HugeiconsIcon icon={FootballIcon} size={14} color={PAGE.winner} strokeWidth={1.8} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: PAGE.text }}>{p.s.goals}</span>
                  </span>
                ) : null}
                <span style={{ minWidth: 52, height: 28, borderRadius: 8, border: `1px solid ${alpha(color, 0.45)}`, background: alpha(color, 0.12), display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 8, fontWeight: 600, letterSpacing: 0.4, color: PAGE.textMuted, lineHeight: '9px' }}>RATING</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color, lineHeight: '15px' }}>{p.rating.toFixed(1)}</span>
                </span>
              </span>
            </div>
          )
        })}
    </div>
  )
  return (
    <div style={{ padding: `14px ${GUTTER}px 0`, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {team('A', game.home, HOME_COLOR)}
      {team('B', game.away, AWAY_COLOR)}
    </div>
  )
}

/* ------------------------------------------------------------------ awards */

function AwardsTab({ game }) {
  return (
    <div style={{ padding: `14px ${GUTTER}px 0` }}>
      <div style={{ ...CARD, paddingTop: 4, paddingBottom: 4 }}>
        {game.awards.map((award, i) => (
          <div key={award.key} style={{ display: 'flex', gap: 12, padding: '14px 0', borderBottom: i < game.awards.length - 1 ? `0.5px solid ${PAGE.divider}` : 0 }}>
            <span style={{ width: 36, height: 36, borderRadius: 10, background: `${award.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <HugeiconsIcon icon={AWARD_ICONS[award.icon]} size={20} color={award.color} strokeWidth={1.8} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 18, lineHeight: '22px', fontWeight: 600, letterSpacing: -0.3, color: PAGE.text }}>{award.label}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: award.color === '#FFD700' ? '#CA9A04' : award.color, marginTop: 2 }}>{award.stat}</div>
              {award.winners.map((name) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <Avatar size={28} />
                  <span style={{ fontSize: 15, fontWeight: 500, color: PAGE.text }}>
                    {name}
                    {name === game.you ? <span style={{ color: PAGE.textMuted }}> · You</span> : null}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ screen */

const PANES = [MyStatsTab, TeamStatsTab, HighlightsTab, LineupsTab, AwardsTab]

export function MatchDetailsPreview({ sport, screenWidth = 288, showHint = true, autoplay = true }) {
  const game = SAMPLE_GAMES[sport] ?? SAMPLE_GAMES.Football
  const scale = screenWidth / W
  const [tab, setTab] = useState(0)
  const [scrollY, setScrollY] = useState(0)
  const [identityH, setIdentityH] = useState(160)
  const [menuOpen, setMenuOpen] = useState(false)
  const paneRefs = useRef([])
  const swipe = useRef(null)

  const selectTab = useCallback((i) => {
    paneRefs.current.forEach((node) => node?.scrollTo({ top: 0 }))
    setScrollY(0)
    setTab(i)
  }, [])

  const { ref, touched, interactionProps } = usePreviewAutoplay(() => {
    if (!autoplay) return
    const pane = paneRefs.current[tab]
    if (pane && pane.scrollTop < 60) pane.scrollTo({ top: 320, behavior: 'smooth' })
    else selectTab((tab + 1) % TABS.length)
  }, 2300)

  useEffect(() => {
    selectTab(0)
  }, [sport, selectTab])

  const offset = Math.min(Math.max(scrollY, 0), identityH)
  const t = identityH ? offset / identityH : 0
  const folded = scrollY > identityH * 0.08
  const headerH = STICKY_H + identityH + TABS_H
  const hinting = showHint && !touched
  const cueTab = hinting ? (tab + 1) % TABS.length : null
  const tabBarTop = headerH - offset - TABS_H
  const cueX = cueTab === null ? 0 : cueTab * (W / TABS.length) + W / TABS.length / 2

  const onPointerDown = (e) => {
    swipe.current = { x: e.clientX, y: e.clientY }
  }
  const onPointerUp = (e) => {
    const start = swipe.current
    swipe.current = null
    if (!start) return
    const dx = (e.clientX - start.x) / scale
    const dy = (e.clientY - start.y) / scale
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      selectTab(Math.min(TABS.length - 1, Math.max(0, tab + (dx < 0 ? 1 : -1))))
    }
  }

  const radius = 47
  return (
    <div
      className={hinting ? 'sbx-anim' : undefined}
      style={{ borderRadius: radius * scale + 6, animation: hinting ? 'sbx-glow 2.4s ease-in-out infinite' : undefined }}
    >
    <PreviewHintStyles />
    <div
      ref={ref}
      {...interactionProps}
      style={{ width: screenWidth + 12, padding: 6, borderRadius: radius * scale + 6, background: '#0b0b0c', boxShadow: '0 24px 48px -24px rgba(7,16,24,0.6), inset 0 0 0 1px rgba(255,255,255,0.08)' }}
    >
      <div style={{ position: 'relative', width: screenWidth, height: H * scale, borderRadius: radius * scale, overflow: 'hidden', background: PAGE.bg, isolation: 'isolate' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, width: W, height: H, transform: `scale(${scale})`, transformOrigin: '0 0', fontFamily: FONT, WebkitFontSmoothing: 'antialiased' }}>
          <div onPointerDown={onPointerDown} onPointerUp={onPointerUp} style={{ position: 'absolute', inset: 0, overflow: 'hidden', touchAction: 'pan-y' }}>
            <div style={{ display: 'flex', width: W * TABS.length, height: H, transform: `translateX(${-tab * W}px)`, transition: `transform 340ms ${EASE}` }}>
              {PANES.map((Pane, i) => (
                <div
                  key={TABS[i].id}
                  ref={(node) => {
                    paneRefs.current[i] = node
                  }}
                  role="tabpanel"
                  aria-hidden={tab !== i}
                  aria-label={TABS[i].label}
                  onScroll={i === tab ? (e) => setScrollY(e.currentTarget.scrollTop) : undefined}
                  className="[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  style={{ width: W, height: H, flexShrink: 0, overflowY: 'auto', overscrollBehavior: 'contain', paddingTop: headerH }}
                >
                  <div style={{ minHeight: H - TABS_H - STICKY_H + 40, paddingBottom: 48 }}>
                    <Pane game={game} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ position: 'absolute', left: 0, top: 0, width: W, height: headerH, zIndex: 10, transform: `translateY(${-offset}px)`, background: '#181317', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, #22645F 0%, #5C5A5D 50%, #574088 100%)' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.22) 16%, rgba(255,255,255,0.05) 40%, rgba(0,0,0,0.42) 70%, rgba(0,0,0,0.84) 100%)' }} />
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
              <TabBar tab={tab} onSelect={selectTab} />
            </div>
          </div>

          <div style={{ position: 'absolute', left: 0, top: STICKY_H, width: W, zIndex: 11, pointerEvents: 'none' }}>
            <MatchHeader game={game} t={t} onMeasure={setIdentityH} />
          </div>

          <StatusBar />
          <div style={{ position: 'absolute', left: 0, right: 0, top: STATUS_H + 4, height: TOP_BAR_H, padding: `0 ${GUTTER}px`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 20 }}>
            <GlassBackButton />
            <InvitePill folded={folded} onOpen={() => setMenuOpen(true)} />
          </div>

          {cueTab !== null ? (
            <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 35, pointerEvents: 'none' }}>
              <span
                className="sbx-anim"
                style={{ position: 'absolute', left: cueX - 20, top: tabBarTop + 2, width: 40, height: 40, borderRadius: 20, background: 'rgba(255,255,255,0.55)', animation: 'sbx-ripple 1.8s ease-out infinite', transition: `left 420ms ${EASE}, top 200ms linear` }}
              />
              <span style={{ position: 'absolute', left: cueX - 17, top: tabBarTop + 20, transition: `left 420ms ${EASE}, top 200ms linear` }}>
                <span className="sbx-anim" style={{ display: 'block', animation: 'sbx-tap 1.8s ease-in-out infinite', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.45))' }}>
                  <Pointer size={40} color="#0C0A09" fill="#fff" strokeWidth={1.4} />
                </span>
              </span>
              <HintBubble
                fontSize={15}
                style={{ top: tabBarTop + 72, left: Math.min(Math.max(cueX - 66, 10), W - 142), transition: `left 420ms ${EASE}, top 200ms linear` }}
              >
                Tap to explore
              </HintBubble>
            </div>
          ) : null}

          <InviteMenu open={menuOpen} keyCode={game.keyCode} onClose={() => setMenuOpen(false)} />
          <span style={{ position: 'absolute', left: (W - 134) / 2, bottom: 8, width: 134, height: 5, borderRadius: 3, background: 'rgba(0,0,0,0.85)', zIndex: 30, pointerEvents: 'none' }} />
        </div>
      </div>
    </div>
    </div>
  )
}
